import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon, type IconName } from './Icon';

// A "⋯" button with a menu, placed against the window so it is never cut off by a header or a pane.

export interface MenuItem {
  key: string;
  label: string;
  icon: IconName;
  danger?: boolean;
  /** Shown as a section title instead of an action. */
  heading?: boolean;
  onSelect?: () => void;
}

export function MenuButton({ label, items, icon = 'more' }: { label: string; items: MenuItem[]; icon?: IconName }) {
  const [open, setOpen] = useState(false);
  const button = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    if (!open || !button.current || !menu.current) return setPos(null);
    const b = button.current.getBoundingClientRect();
    const { offsetWidth: w, offsetHeight: h } = menu.current;
    const top = b.bottom + 6 + h > window.innerHeight - 8 ? Math.max(8, b.top - 6 - h) : b.bottom + 6;
    setPos({ top, left: Math.min(Math.max(8, b.right - w), window.innerWidth - w - 8) });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    menu.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    const down = (e: PointerEvent) => {
      const t = e.target as Node;
      if (!menu.current?.contains(t) && !button.current?.contains(t)) setOpen(false);
    };
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        button.current?.focus();
      }
    };
    const close = () => setOpen(false);
    document.addEventListener('pointerdown', down);
    document.addEventListener('keydown', key);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('pointerdown', down);
      document.removeEventListener('keydown', key);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  return (
    <>
      <button ref={button} className="icon-btn" type="button" aria-label={label} title={label} aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((x) => !x)}>
        <Icon name={icon} />
      </button>
      {open &&
        createPortal(
          <div ref={menu} className="msg-popover" style={pos ? { top: pos.top, left: pos.left } : { visibility: 'hidden' }}>
            <div className="msg-menu" role="menu" aria-label={label}>
              {items.map((item) =>
                item.heading ? (
                  <span key={item.key} className="menu-heading" role="presentation">
                    {item.label}
                  </span>
                ) : (
                  <button
                    key={item.key}
                    type="button"
                    role="menuitem"
                    className={item.danger ? 'danger' : undefined}
                    onClick={() => {
                      setOpen(false);
                      item.onSelect?.();
                    }}
                  >
                    <Icon name={item.icon} size={18} /> <span>{item.label}</span>
                  </button>
                ),
              )}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
